import { useState, useEffect } from 'react';
import { 
    Modal, ModalDialog, ModalClose, DialogTitle, DialogContent, 
    Stack, FormControl, FormLabel, Input, Button, Switch, 
    Typography, Box, Chip, Select, Option, Alert, ModalOverflow 
} from '@mui/joy';
import { AnimatePresence, motion } from 'framer-motion';

export default function UserModal({ 
    open, 
    user, 
    onClose, 
    onSave, 
    loading = false 
}) {
    const [formData, setFormData] = useState({
        email: '',
        first_name: '',
        last_name: '',
        plan: 'free',
        role: 'user',
        is_active: true,
        password: ''
    });
    const [errors, setErrors] = useState({});

    // Сбрасываем форму при открытии/закрытии модального окна
    useEffect(() => {
        if (open) {
            if (user) {
                // Режим редактирования
                setFormData({
                    email: user.email || '',
                    first_name: user.first_name || '',
                    last_name: user.last_name || '',
                    plan: user.plan || 'free',
                    role: user.role || 'user',
                    is_active: user.is_active !== undefined ? user.is_active : true,
                    password: '' // Пустой пароль - не изменять
                });
            } else {
                // Режим создания
                setFormData({
                    email: '',
                    first_name: '',
                    last_name: '',
                    plan: 'free',
                    role: 'user',
                    is_active: true,
                    password: ''
                });
            }
            setErrors({});
        }
    }, [open, user]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Валидация
        const newErrors = {};
        if (!formData.email.trim()) newErrors.email = 'Email обязателен';
        if (!formData.first_name.trim()) newErrors.first_name = 'Имя обязательно';
        // if (!formData.last_name.trim()) newErrors.last_name = 'Фамилия обязательна';
        if (!user && !formData.password.trim()) newErrors.password = 'Пароль обязателен при создании';
        
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        // Подготавливаем данные для отправки
        const dataToSend = { ...formData };
        
        // Если пароль пустой и это редактирование - убираем его из данных
        if (user && !dataToSend.password.trim()) {
            delete dataToSend.password;
        }
        
        await onSave(dataToSend);
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Очищаем ошибку при изменении поля
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const isEditing = !!user;
    const title = isEditing ? 'Редактирование пользователя' : 'Создание пользователя';

    return (
        <AnimatePresence>
            {open && (
                <Modal 
                    open={open} 
                    onClose={onClose}
                    slotProps={{
                        backdrop: {
                            component: motion.div,
                            initial: { opacity: 0, backdropFilter: 'blur(0px)' },
                            animate: { opacity: 1, backdropFilter: 'blur(8px)' },
                            exit: { opacity: 0, backdropFilter: 'blur(0px)' },
                            transition: { duration: 0.3 },
                        },
                    }}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10012
                    }}
                >
                    <ModalOverflow sx={{ height: 'auto' }}>
                        <ModalDialog
                            component={motion.div}
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            transition={{ duration: 0.3, ease: "easeOut" }}
                            sx={{
                                width: '90%',
                                maxWidth: 500,
                                maxHeight: '90vh',
                                overflow: 'auto'
                            }}
                        >
                            <ModalClose />
                            
                            <DialogTitle>
                                {title}
                                {user && (
                                    <Typography level="body2" color="neutral">
                                        ID: {user.id}
                                    </Typography>
                                )}
                            </DialogTitle>

                            <DialogContent>
                                <Stack spacing={3} component="form" onSubmit={handleSubmit}>
                                    {/* Информационное сообщение для создания */}
                                    {!isEditing && (
                                        <Alert color="info" variant="soft">
                                            <Typography level="body2">
                                                При создании пользователя будет отправлено приветственное письмо с инструкциями.
                                            </Typography>
                                        </Alert>
                                    )}

                                    {/* Email */}
                                    <FormControl required error={!!errors.email}>
                                        <FormLabel>Email</FormLabel>
                                        <Input
                                            value={formData.email}
                                            onChange={(e) => handleChange('email', e.target.value)}
                                            type="email"
                                            placeholder="user@example.com"
                                            disabled={loading}
                                        />
                                        {errors.email && (
                                            <Typography level="body2" color="danger" sx={{ mt: 0.5 }}>
                                                {errors.email}
                                            </Typography>
                                        )}
                                    </FormControl>

                                    {/* Имя и Фамилия */}
                                    <Stack direction="row" spacing={2}>
                                        <FormControl required error={!!errors.first_name} sx={{ flex: 1 }}>
                                            <FormLabel>Имя</FormLabel>
                                            <Input
                                                value={formData.first_name}
                                                onChange={(e) => handleChange('first_name', e.target.value)}
                                                placeholder="Иван"
                                                disabled={loading}
                                            />
                                            {errors.first_name && (
                                                <Typography level="body2" color="danger" sx={{ mt: 0.5 }}>
                                                    {errors.first_name}
                                                </Typography>
                                            )}
                                        </FormControl>
                                        <FormControl error={!!errors.last_name} sx={{ flex: 1 }}>
                                            <FormLabel>Фамилия</FormLabel>
                                            <Input
                                                value={formData.last_name}
                                                onChange={(e) => handleChange('last_name', e.target.value)}
                                                placeholder="Иванов"
                                                disabled={loading}
                                            />
                                            {errors.last_name && (
                                                <Typography level="body2" color="danger" sx={{ mt: 0.5 }}>
                                                    {errors.last_name}
                                                </Typography>
                                            )}
                                        </FormControl>
                                    </Stack>

                                    {/* Пароль */}
                                    <FormControl error={!!errors.password}>
                                        <FormLabel>
                                            Пароль {!isEditing && <Typography color="danger">*</Typography>}
                                        </FormLabel>
                                        <Input
                                            value={formData.password}
                                            onChange={(e) => handleChange('password', e.target.value)}
                                            type="password"
                                            placeholder={isEditing ? "Оставьте пустым, чтобы не изменять" : "Введите пароль"}
                                            disabled={loading}
                                        />
                                        {errors.password && (
                                            <Typography level="body2" color="danger" sx={{ mt: 0.5 }}>
                                                {errors.password}
                                            </Typography>
                                        )}
                                        {isEditing && (
                                            <Typography level="body2" color="neutral" sx={{ mt: 0.5 }}>
                                                Введите новый пароль для сброса текущего
                                            </Typography>
                                        )}
                                    </FormControl>

                                    {/* Тарифный план */}
                                    <FormControl required>
                                        <FormLabel>Тарифный план</FormLabel>
                                        <Select
                                            slotProps={{ listbox: { sx: { zIndex: 10013 } } }}
                                            value={formData.plan}
                                            onChange={(e, value) => handleChange('plan', value)}
                                            disabled={loading}
                                        >
                                            <Option value="free">
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {/* <Chip size="sm" variant="soft" color="neutral">
                                                        Бесплатный
                                                    </Chip> */}
                                                    <Typography level="body2" color="neutral">
                                                        Базовые функции
                                                    </Typography>
                                                </Box>
                                            </Option>
                                            <Option value="plan1">
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {/* <Chip size="sm" variant="soft" color="primary">
                                                        План 1
                                                    </Chip> */}
                                                    <Typography level="body2" color="neutral">
                                                        Расширенные возможности
                                                    </Typography>
                                                </Box>
                                            </Option>
                                        </Select>
                                    </FormControl>

                                    {/* Роль */}
                                    <FormControl required>
                                        <FormLabel>Роль</FormLabel>
                                        <Select
                                            slotProps={{ listbox: { sx: { zIndex: 10013 } } }}
                                            value={formData.role}
                                            onChange={(e, value) => handleChange('role', value)}
                                            disabled={loading}
                                        >
                                            <Option value="user">
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography level="body2" color="neutral">
                                                        Пользователь
                                                    </Typography>
                                                </Box>
                                            </Option>
                                            <Option value="admin">
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography level="body2" color="neutral">
                                                        Администратор
                                                    </Typography>
                                                </Box>
                                            </Option>
                                        </Select>
                                    </FormControl>

                                    {/* Активность */}
                                    <FormControl>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Switch
                                                checked={formData.is_active}
                                                onChange={(e) => handleChange('is_active', e.target.checked)}
                                                disabled={loading}
                                                color={formData.is_active ? 'success' : 'neutral'}
                                            />
                                            <Box sx={{ flex: 1 }}>
                                                <FormLabel>Активный аккаунт</FormLabel>
                                                <Typography level="body2" color="neutral">
                                                    {formData.is_active 
                                                        ? 'Пользователь может входить в систему и использовать все функции' 
                                                        : 'Пользователь заблокирован и не может войти в систему'}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </FormControl>

                                    {/* Предупреждение для администраторов */}
                                    {formData.role === 'admin' && (
                                        <Alert color="warning" variant="soft">
                                            <Typography level="body2">
                                                <strong>Внимание:</strong> Администраторы имеют полный доступ ко всем функциям системы, 
                                                включая управление пользователями, категориями и вопросами.
                                            </Typography>
                                        </Alert>
                                    )}

                                    {/* Кнопки */}
                                    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                                        <Button
                                            variant="outlined"
                                            color="neutral"
                                            onClick={onClose}
                                            disabled={loading}
                                        >
                                            Отмена
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="solid"
                                            color="primary"
                                            loading={loading}
                                            disabled={!formData.email.trim() || !formData.first_name.trim() || (!isEditing && !formData.password.trim())}
                                        >
                                            {isEditing ? 'Сохранить изменения' : 'Создать пользователя'}
                                        </Button>
                                    </Stack>
                                </Stack>
                            </DialogContent>
                        </ModalDialog>
                    </ModalOverflow>
                </Modal>
            )}
        </AnimatePresence>
    );
}